/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Upload, FileText, Trash2, Download, Database, 
  CheckCircle, AlertCircle, Loader, FolderOpen,
  Plus, RefreshCw, Search, Filter, ArrowLeft,
  ChevronRight, HardDrive, BarChart3, Sparkles
} from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import './TrainingDataManagement.css';

// ═══════════════════════════════════════════════════════════════════════════
// API Configuration
// ═══════════════════════════════════════════════════════════════════════════
const PRODUCTION_API_BASE = 'https://skilldelivery-platform.onrender.com';
const DEVELOPMENT_API_BASE = 'http://localhost:5000';

const isProduction = typeof window !== 'undefined' && 
                     window.location.hostname !== 'localhost' &&
                     window.location.hostname !== '127.0.0.1';

const API_BASE = isProduction ? PRODUCTION_API_BASE : DEVELOPMENT_API_BASE;

// Admin auth also needs the 2FA session header when the admin has 2FA on.
// adminApi.js adds it for axios calls; plain fetch calls must add it too.
const twoFAHeader = () => {
  const s = localStorage.getItem('admin2FASession');
  return s ? { 'x-2fa-session': s } : {};
};

console.log('[TrainingDataMgmt] API Base:', API_BASE);

const DOMAINS = [
  { value: 'WMS', label: 'WMS (Warehouse Management)', color: '#3b82f6' },
  { value: 'iSeries', label: 'iSeries (AS/400)', color: '#f59e0b' },
  { value: 'Civil', label: 'Civil Engineering', color: '#10b981' },
  { value: 'Stitching', label: 'Stitching/Apparel', color: '#ec4899' },
  { value: 'Platform', label: 'Platform General', color: '#8b5cf6' },
  { value: 'SkillCode', label: 'SkillCode (AI Software Engineering)', color: '#7c3aed' }
];

// Source format options for data ingestion
const SOURCE_FORMATS = [
  { value: 'Auto', label: '🔍 Auto-Detect', description: 'Automatically detect format from file structure' },
  { value: 'Manual', label: '📝 Manual/Alpaca', description: 'Standard training format (instruction/input/output)' },
  { value: 'ChatGPT', label: '🤖 ChatGPT Export', description: 'OpenAI ChatGPT conversation exports' },
  { value: 'Claude', label: '🧠 Claude Export', description: 'Anthropic Claude conversation exports' },
  { value: 'Gemini', label: '💎 Gemini Export', description: 'Google Gemini/Bard exports' },
  { value: 'Copilot', label: '✈️ Copilot Export', description: 'GitHub Copilot conversation exports' }
];

export default function TrainingDataManagement() {
  // State
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Filters
  const [filterDomain, setFilterDomain] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Upload modal
  const [showUpload, setShowUpload] = useState(false);
  const [uploadMode, setUploadMode] = useState('single');
  const [uploadDomain, setUploadDomain] = useState('WMS');
  const [uploadName, setUploadName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [mergeFiles, setMergeFiles] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  // Source format selection
  const [sourceFormat, setSourceFormat] = useState('Auto');
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);
  
  // Stats
  const [stats, setStats] = useState(null);

  // ══════════════════════════════════════════════════════════════════════════
  // FETCH DATA
  // ══════════════════════════════════════════════════════════════════════════

  const fetchDatasets = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterDomain) params.append('domain', filterDomain);
      
      const token = localStorage.getItem('adminAccessToken');
      const response = await fetch(`${API_BASE}/api/admin/training/datasets?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`, ...twoFAHeader()
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setDatasets(data.datasets);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to fetch datasets');
    } finally {
      setLoading(false);
    }
  }, [filterDomain]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminAccessToken');
      const response = await fetch(`${API_BASE}/api/admin/training/datasets/stats/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`, ...twoFAHeader()
        }
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.summary);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    fetchDatasets();
    fetchStats();
  }, [fetchDatasets]);

  // Auto-clear messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // ══════════════════════════════════════════════════════════════════════════
  // UPLOAD HANDLERS
  // ══════════════════════════════════════════════════════════════════════════

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setUploadFiles(files);
    setValidationResult(null);
    
    if (uploadMode === 'single' && files.length === 1) {
      setUploadName(files[0].name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleValidate = async () => {
    if (uploadFiles.length === 0) {
      setError('Please select file(s) to validate');
      return;
    }
    
    setValidating(true);
    setValidationResult(null);
    
    try {
      const formData = new FormData();
      formData.append('sourceFormat', sourceFormat);
      
      uploadFiles.forEach(file => {
        formData.append('files', file);
      });
      
      const token = localStorage.getItem('adminAccessToken');
      const response = await fetch(`${API_BASE}/api/admin/training/datasets/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`, ...twoFAHeader()
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setValidationResult(data.validation);
      } else {
        setError(data.error || 'Validation failed');
      }
    } catch (err) {
      setError('Validation failed: ' + err.message);
    } finally {
      setValidating(false);
    }
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      setError('Please select file(s) to upload');
      return;
    }
    
    setUploading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('domain', uploadDomain);
      formData.append('sourceFormat', sourceFormat);
      
      const token = localStorage.getItem('adminAccessToken');
      
      if (uploadMode === 'single') {
        formData.append('file', uploadFiles[0]);
        formData.append('name', uploadName || uploadFiles[0].name);
        formData.append('description', uploadDescription);
        
        const response = await fetch(`${API_BASE}/api/admin/training/datasets/store`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`, ...twoFAHeader()
          },
          body: formData
        });
        
        const data = await response.json();
        
        if (data.success) {
          const formatInfo = data.conversion?.detectedFormat 
            ? ` (converted from ${data.conversion.detectedFormat})` 
            : '';
          setSuccess(`Dataset "${data.dataset.name}" uploaded successfully (${data.dataset.recordCount} records${formatInfo})`);
          resetUploadForm();
          fetchDatasets();
          fetchStats();
        } else {
          setError(data.error || 'Upload failed');
        }
      } else {
        uploadFiles.forEach(file => {
          formData.append('files', file);
        });
        formData.append('name', uploadName || `${uploadDomain} Combined Dataset`);
        formData.append('description', uploadDescription);
        formData.append('mergeFiles', mergeFiles.toString());
        
        const response = await fetch(`${API_BASE}/api/admin/training/datasets/store-multi`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`, ...twoFAHeader()
          },
          body: formData
        });
        
        const data = await response.json();
        
        if (data.success) {
          if (mergeFiles && data.dataset) {
            const formatsInfo = data.conversion?.formatsDetected?.length > 0
              ? ` (sources: ${data.conversion.formatsDetected.join(', ')})`
              : '';
            setSuccess(`${uploadFiles.length} files merged into "${data.dataset.name}" (${data.dataset.recordCount} records${formatsInfo})`);
          } else {
            setSuccess(data.message);
          }
          resetUploadForm();
          fetchDatasets();
          fetchStats();
        } else {
          setError(data.error || 'Upload failed');
        }
      }
    } catch (err) {
      setError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setShowUpload(false);
    setUploadFiles([]);
    setUploadName('');
    setUploadDescription('');
    setUploadMode('single');
    setSourceFormat('Auto');
    setValidationResult(null);
  };

  // ══════════════════════════════════════════════════════════════════════════
  // DELETE HANDLER
  // ══════════════════════════════════════════════════════════════════════════

  const handleDelete = async (dataset) => {
    if (!window.confirm(`Delete dataset "${dataset.name}"?\n\nThis cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('adminAccessToken');
      const response = await fetch(`${API_BASE}/api/admin/training/datasets/${dataset._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`, ...twoFAHeader()
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Dataset "${dataset.name}" deleted`);
        fetchDatasets();
        fetchStats();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Delete failed: ' + err.message);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // DOWNLOAD HANDLER
  // ══════════════════════════════════════════════════════════════════════════

  // Admin auth reads the Authorization header only, so a plain window.open
  // (with the token in the URL) would get 401. Fetch with the header and
  // hand the file to the browser instead (security audit Oct 2026, C1).
  const handleDownload = async (dataset) => {
    try {
      const token = localStorage.getItem('adminAccessToken');
      const response = await fetch(`${API_BASE}/api/admin/training/datasets/${dataset._id}/download`, {
        headers: { 'Authorization': `Bearer ${token}`, ...twoFAHeader() }
      });
      if (!response.ok) throw new Error(`Download failed (${response.status})`);
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const fileName = match ? match[1] : `${dataset.name || dataset._id}.jsonl`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      setError(err.message);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // FILTERED DATASETS
  // ══════════════════════════════════════════════════════════════════════════

  const filteredDatasets = datasets.filter(d => {
    if (searchTerm && !d.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════

  return (
    <AdminLayout>
      <div className="training-data-page">
        {/* Breadcrumb */}
        <div className="tdm-breadcrumb">
          <Link to="/admin/dashboard">Admin</Link>
          <ChevronRight size={16} />
          <Link to="/admin/training/jobs">Training</Link>
          <ChevronRight size={16} />
          <span>Data Management</span>
        </div>

        {/* Header */}
        <div className="tdm-header">
          <div className="tdm-title">
            <Database size={28} />
            <div>
              <h1>Training Data Management</h1>
              <p>Upload and manage training datasets stored in MongoDB</p>
            </div>
          </div>
          
          <div className="tdm-header-actions">
            <Link to="/admin/training/submit" className="btn-secondary">
              <ArrowLeft size={18} />
              Back to Submit Job
            </Link>
            <button className="btn-primary" onClick={() => setShowUpload(true)}>
              <Plus size={18} />
              Upload Dataset
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert alert-error">
            <AlertCircle size={20} />
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}
        
        {success && (
          <div className="alert alert-success">
            <CheckCircle size={20} />
            <span>{success}</span>
            <button onClick={() => setSuccess(null)}>×</button>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="tdm-stats">
            <div className="stat-card stat-total">
              <div className="stat-icon">
                <FolderOpen size={24} />
              </div>
              <div className="stat-info">
                <span className="stat-value">{stats.totalDatasets}</span>
                <span className="stat-label">Total Datasets</span>
              </div>
            </div>
            
            <div className="stat-card stat-records">
              <div className="stat-icon">
                <BarChart3 size={24} />
              </div>
              <div className="stat-info">
                <span className="stat-value">{stats.totalRecords?.toLocaleString()}</span>
                <span className="stat-label">Total Records</span>
              </div>
            </div>
            
            <div className="stat-card stat-size">
              <div className="stat-icon">
                <HardDrive size={24} />
              </div>
              <div className="stat-info">
                <span className="stat-value">{stats.totalSize}</span>
                <span className="stat-label">Total Size</span>
              </div>
            </div>
            
            <div className="stat-card stat-success">
              <div className="stat-icon">
                <CheckCircle size={24} />
              </div>
              <div className="stat-info">
                <span className="stat-value">{datasets.filter(d => d.hasContent).length} / {datasets.length}</span>
                <span className="stat-label">With Content</span>
              </div>
            </div>
          </div>
        )}

        {/* Domain Stats */}
        {stats?.byDomain && stats.byDomain.length > 0 && (
          <div className="tdm-domain-stats">
            {stats.byDomain.map(d => (
              <div key={d.domain} className={`domain-stat domain-${d.domain?.toLowerCase()}`}>
                <span className="domain-name">{d.domain}</span>
                <span className="domain-count">{d.records?.toLocaleString()} records</span>
                <span className="domain-datasets">{d.datasets} datasets</span>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="tdm-filters">
          <div className="filter-group">
            <Filter size={18} />
            <select 
              value={filterDomain} 
              onChange={(e) => setFilterDomain(e.target.value)}
            >
              <option value="">All Domains</option>
              {DOMAINS.map(d => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>
          
          <div className="search-group">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search datasets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <button className="btn-icon" onClick={() => { fetchDatasets(); fetchStats(); }}>
            <RefreshCw size={18} />
          </button>
        </div>

        {/* Table */}
        <div className="tdm-table-container">
          {loading ? (
            <div className="tdm-loading">
              <Loader className="spin" size={32} />
              <p>Loading datasets...</p>
            </div>
          ) : filteredDatasets.length === 0 ? (
            <div className="tdm-empty">
              <FolderOpen size={48} />
              <h3>No Datasets Found</h3>
              <p>{searchTerm || filterDomain ? 'Try adjusting your filters' : 'Upload your first training dataset'}</p>
              <button className="btn-primary" onClick={() => setShowUpload(true)}>
                <Plus size={18} />
                Upload Dataset
              </button>
            </div>
          ) : (
            <table className="tdm-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Domain</th>
                  <th>Records</th>
                  <th>Size</th>
                  <th>Content</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDatasets.map(dataset => (
                  <tr key={dataset._id}>
                    <td>
                      <div className="dataset-name">
                        <FileText size={16} />
                        <div>
                          <span className="name">{dataset.name}</span>
                          <span className="filename">{dataset.fileName}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`domain-badge domain-${dataset.domain?.toLowerCase()}`}>
                        {dataset.domain}
                      </span>
                    </td>
                    <td>{dataset.recordCount?.toLocaleString() || 0}</td>
                    <td>{dataset.fileSize ? `${(dataset.fileSize / 1024).toFixed(1)} KB` : '-'}</td>
                    <td>
                      {dataset.hasContent ? (
                        <span className="content-badge content-yes">
                          <CheckCircle size={14} />
                          Stored
                        </span>
                      ) : (
                        <span className="content-badge content-no">
                          <AlertCircle size={14} />
                          Empty
                        </span>
                      )}
                    </td>
                    <td>{new Date(dataset.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        {dataset.hasContent && (
                          <button 
                            className="btn-icon" 
                            onClick={() => handleDownload(dataset)}
                            title="Download"
                          >
                            <Download size={16} />
                          </button>
                        )}
                        <button 
                          className="btn-icon btn-danger" 
                          onClick={() => handleDelete(dataset)}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Upload Modal */}
        {showUpload && (
          <div className="modal-overlay" onClick={() => !uploading && resetUploadForm()}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>
                  <Upload size={24} />
                  Upload Training Data
                </h2>
                <button 
                  className="modal-close" 
                  onClick={resetUploadForm}
                  disabled={uploading}
                >
                  ×
                </button>
              </div>
              
              <div className="modal-body">
                {/* Upload Mode Toggle */}
                <div className="upload-mode-toggle">
                  <button 
                    className={uploadMode === 'single' ? 'active' : ''}
                    onClick={() => { setUploadMode('single'); setUploadFiles([]); setValidationResult(null); }}
                  >
                    Single File
                  </button>
                  <button 
                    className={uploadMode === 'multiple' ? 'active' : ''}
                    onClick={() => { setUploadMode('multiple'); setUploadFiles([]); setValidationResult(null); }}
                  >
                    Multiple Files
                  </button>
                </div>

                <div className="upload-info-box">
                  <strong>💡 Multi-Source Support</strong>
                  <p>Upload files from ChatGPT, Claude, Gemini, or Copilot. They'll be automatically converted to training format.</p>
                </div>
                
                {/* Domain Selection */}
                <div className="form-group">
                  <label>Domain *</label>
                  <select 
                    value={uploadDomain} 
                    onChange={(e) => setUploadDomain(e.target.value)}
                  >
                    {DOMAINS.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>

                {/* Source Format Selection */}
                <div className="form-group">
                  <label>Source Format *</label>
                  <select 
                    value={sourceFormat} 
                    onChange={(e) => { setSourceFormat(e.target.value); setValidationResult(null); }}
                  >
                    {SOURCE_FORMATS.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                  <p className="help-text">
                    {SOURCE_FORMATS.find(f => f.value === sourceFormat)?.description}
                  </p>
                </div>
                
                {/* File Selection */}
                <div className="form-group">
                  <label>
                    {uploadMode === 'single' ? 'Select File *' : 'Select Files *'}
                  </label>
                  <div className="file-input-wrapper">
                    <input
                      type="file"
                      accept=".json,.jsonl,.txt"
                      multiple={uploadMode === 'multiple'}
                      onChange={handleFileSelect}
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="file-input-label">
                      <Upload size={20} />
                      {uploadFiles.length === 0 
                        ? 'Choose JSON/JSONL file(s)'
                        : `${uploadFiles.length} file(s) selected`
                      }
                    </label>
                  </div>
                  {uploadFiles.length > 0 && (
                    <div className="selected-files">
                      {uploadFiles.map((f, i) => (
                        <div key={i} className="selected-file">
                          <FileText size={14} />
                          <span className="file-name">{f.name}</span>
                          <span className="file-size">({(f.size / 1024).toFixed(1)} KB)</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Validate Button */}
                {uploadFiles.length > 0 && (
                  <div className="form-group">
                    <button 
                      className="btn-secondary"
                      onClick={handleValidate}
                      disabled={validating}
                      style={{ width: '100%', justifyContent: 'center' }}
                    >
                      {validating ? (
                        <>
                          <Loader className="spin" size={16} />
                          Validating...
                        </>
                      ) : (
                        <>
                          <Sparkles size={16} />
                          Validate & Preview
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Validation Results */}
                {validationResult && (
                  <div style={{
                    background: validationResult.validFiles === validationResult.filesChecked ? '#f0fdf4' : '#fefce8',
                    border: `1px solid ${validationResult.validFiles === validationResult.filesChecked ? '#86efac' : '#fde047'}`,
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px', 
                      marginBottom: '12px', 
                      color: validationResult.validFiles === validationResult.filesChecked ? '#16a34a' : '#ca8a04' 
                    }}>
                      <CheckCircle size={18} />
                      <span>
                        <strong>{validationResult.validFiles}/{validationResult.filesChecked}</strong> files valid • 
                        <strong> {validationResult.totalRecords}</strong> records found
                      </span>
                    </div>
                    {validationResult.results.map((r, i) => (
                      <div key={i} style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 12px',
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderLeft: `3px solid ${r.valid ? '#22c55e' : '#f59e0b'}`,
                        borderRadius: '6px',
                        marginBottom: '8px',
                        fontSize: '13px'
                      }}>
                        <span style={{ fontWeight: 600, color: '#1e293b', flex: 1, minWidth: '150px' }}>{r.fileName}</span>
                        <span style={{ color: '#6366f1', background: '#eef2ff', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                          Format: {r.detectedFormat}
                        </span>
                        <span style={{ color: '#64748b', fontSize: '12px' }}>{r.recordCount} records</span>
                        {r.errors && r.errors.length > 0 && (
                          <div style={{ width: '100%', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
                            {r.errors.map((e, j) => (
                              <div key={j} style={{ color: '#dc2626', fontSize: '12px', padding: '2px 0' }}>{e}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Merge Option (multiple files only) */}
                {uploadMode === 'multiple' && uploadFiles.length > 1 && (
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={mergeFiles}
                        onChange={(e) => setMergeFiles(e.target.checked)}
                      />
                      <span>Merge into single dataset (recommended)</span>
                    </label>
                    <p className="help-text">
                      Combines all files into one dataset for training. Records are shuffled for better training.
                    </p>
                  </div>
                )}
                
                {/* Dataset Name */}
                <div className="form-group">
                  <label>Dataset Name</label>
                  <input
                    type="text"
                    value={uploadName}
                    onChange={(e) => setUploadName(e.target.value)}
                    placeholder={mergeFiles ? `${uploadDomain} Combined Dataset` : 'Auto-generated from filename'}
                  />
                </div>
                
                {/* Description */}
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    placeholder="Optional description..."
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="modal-footer">
                <button 
                  className="btn-secondary" 
                  onClick={resetUploadForm}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleUpload}
                  disabled={uploading || uploadFiles.length === 0}
                >
                  {uploading ? (
                    <>
                      <Loader className="spin" size={18} />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      Upload to MongoDB
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
